// client/src/services/AdminToolsService.js
import BaseService from './BaseService'

/**
 * Service for admin database tool operations
 * Manages seeding, resetting, and cleanup operations for testing and development
 */
class AdminToolsService extends BaseService {
  constructor() {
    super('/api/1.0/admin-tools')
  }

  /**
   * Seed the database with test data
   * @param {Object} options - Seed configuration
   * @param {string} options.tag - Tag to identify seeded data
   * @param {string} options.adminEmail - Email of admin performing the seed
   * @param {boolean} [options.withCompleted] - Include completed matches
   * @param {boolean} [options.withScheduled] - Include scheduled matches
   * @param {boolean} [options.withAvailability] - Include availability data
   * @param {number} [options.completedPercent] - Percentage of matches to mark completed
   * @param {number} [options.scheduledPercent] - Percentage of matches to schedule
   * @param {number} [options.availabilityPerPlayer] - Availability entries per player
   * @param {boolean} [options.useNewLeagues] - Use new league creation logic
   * @param {'TRADITIONAL'|'PRO_SET_8'} [options.matchFormat] - Match format to apply to seeded leagues
   * @returns {Promise<{success: boolean, stdout: string, stderr: string}>}
   */
  async seed(options) {
    return this.post('/seed', options)
  }

  /**
   * Reset seeded data by tag
   * @param {Object} options - Reset configuration
   * @param {string} options.tag - Tag identifying data to reset
   * @returns {Promise<{success: boolean, stdout: string, stderr: string}>}
   */
  async reset(options) {
    return this.post('/reset', options)
  }

  /**
   * Seed large-scale test data
   * @param {Object} options - Large seed configuration
   * @param {string} options.tag - Tag to identify seeded data
   * @param {number} [options.numPlayers] - Number of players to create
   * @param {number} [options.divisionsPerLeague] - Divisions per league
   * @returns {Promise<{success: boolean, stdout: string, stderr: string}>}
   */
  async seedLargeScale(options) {
    return this.post('/seed-large-scale', options)
  }

  /**
   * Reset large-scale seeded data
   * @param {Object} options - Reset configuration
   * @param {string} options.tag - Tag identifying data to reset
   * @returns {Promise<{success: boolean, stdout: string, stderr: string}>}
   */
  async resetLargeScale(options) {
    return this.post('/reset-large-scale', options)
  }

  /**
   * Clear all seeded data (dangerous!)
   * @returns {Promise<{success: boolean, stdout: string, stderr: string}>}
   */
  async clearAll() {
    return this.post('/clear-all-seeds')
  }

  /**
   * Expire stale match proposals
   * @param {Object} [options] - Expiration options
   * @param {number} [options.batchSize=100] - Number of proposals to process
   * @returns {Promise<{success: boolean, data: {message: string}}>}
   */
  async expireProposals(options = { batchSize: 100 }) {
    return this.post('/expire-proposals', options)
  }

  async sendTestSms(options) {
    return this.post('/send-test-sms', options)
  }

  async demoSeed() {
    return this.post('/demo-seed')
  }

  async demoReset() {
    return this.post('/demo-reset')
  }

  async demoRegisterPlayers() {
    return this.post('/demo-register-players')
  }

  async demoAssignTeams() {
    return this.post('/demo-assign-teams')
  }

  async demoOpenProposals() {
    return this.post('/demo-open-proposals')
  }

  async demoAcceptProposal() {
    return this.post('/demo-accept-proposal')
  }

  async demoEnterScores() {
    return this.post('/demo-enter-scores')
  }

  async demoRunAll(options = {}) {
    return this.post('/demo-run-all', options)
  }

  async skillDemoSeed() {
    return this.post('/skill-demo-seed')
  }

  async skillDemoReset() {
    return this.post('/skill-demo-reset')
  }

  async skillDemoRunAll(options = {}) {
    return this.post('/skill-demo-run-all', options)
  }
}

// Export singleton instance
const adminToolsService = new AdminToolsService()
export default adminToolsService
